import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_CATEGORIES,
  type Account,
  type Category,
  type MerchantRule,
  type Settings,
  type Transaction,
} from '@wis/shared';
import {
  DEFAULT_SETTINGS,
  SETTINGS_KEY,
  db,
  ensureSeeded,
  writeSettings,
} from '../db/database.js';
import { buildMerchantHistory, type MerchantHistoryEntry } from '../db/repository.js';
import { readSyncState, runSync, type SyncState } from '../sync/engine.js';

interface AppContextValue {
  ready: boolean;
  settings: Settings;
  categories: Category[];
  accounts: Account[];
  rules: MerchantRule[];
  transactions: Transaction[];
  merchantHistory: MerchantHistoryEntry[];
  reviewCount: number;
  sync: SyncState;
  online: boolean;
  updateSettings: (patch: Partial<Settings>) => Promise<void>;
  refreshSync: () => Promise<void>;
  toast: (message: string, action?: { label: string; onAction: () => void }) => void;
  dismissToast: () => void;
  toastState: ToastState | null;
}

export interface ToastState {
  message: string;
  action?: { label: string; onAction: () => void };
  id: number;
}

const AppContext = createContext<AppContextValue | null>(null);

const EMPTY_SYNC: SyncState = {
  status: 'disabled',
  pendingCount: 0,
  failedCount: 0,
  lastSyncedAt: null,
  lastError: null,
};

/** Shortest gap between two unrequested synchronisations. */
const AUTO_SYNC_MIN_INTERVAL_MS = 20_000;

export function AppProvider({ children }: { children: ReactNode }): JSX.Element {
  const [ready, setReady] = useState(false);
  const [sync, setSync] = useState<SyncState>(EMPTY_SYNC);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [toastState, setToastState] = useState<ToastState | null>(null);

  useEffect(() => {
    let cancelled = false;
    ensureSeeded()
      .catch(() => {
        // A seeding failure must not leave the app stuck on a spinner; the
        // defaults below keep it usable and Settings can repair it.
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const settingsRecord = useLiveQuery(() => db.meta.get(SETTINGS_KEY), [], undefined);
  const categories = useLiveQuery(() => db.categories.toArray(), [], undefined);
  const accounts = useLiveQuery(() => db.accounts.toArray(), [], undefined);
  const rules = useLiveQuery(() => db.merchantRules.toArray(), [], undefined);
  const transactions = useLiveQuery(() => db.transactions.toArray(), [], undefined);
  const merchantHistory = useLiveQuery(() => buildMerchantHistory(), [], undefined);

  const settings = useMemo<Settings>(
    () => ({ ...DEFAULT_SETTINGS, ...((settingsRecord?.value as Partial<Settings>) ?? {}) }),
    [settingsRecord],
  );

  const sortedCategories = useMemo(
    () =>
      (categories && categories.length > 0 ? categories : DEFAULT_CATEGORIES)
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [categories],
  );

  const activeTransactions = useMemo(
    () =>
      (transactions ?? [])
        .filter((transaction) => !transaction.deletedAt)
        .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)),
    [transactions],
  );

  const reviewCount = useMemo(
    () =>
      activeTransactions.filter((transaction) => transaction.reviewStatus === 'needs-review').length,
    [activeTransactions],
  );

  /**
   * Refresh the sync summary, but only commit a state change when something
   * actually differs.
   *
   * This runs whenever the ledger changes, which is often. Setting a fresh
   * object every time would re-render the whole tree for no reason -- and a
   * re-render arriving between a keystroke and React's own commit can revert a
   * controlled input the user is still typing into.
   */
  const refreshSync = useCallback(async () => {
    const next = await readSyncState();
    setSync((current) =>
      current.status === next.status &&
      current.pendingCount === next.pendingCount &&
      current.failedCount === next.failedCount &&
      current.lastSyncedAt === next.lastSyncedAt &&
      current.lastError === next.lastError
        ? current
        : next,
    );
  }, []);

  useEffect(() => {
    void refreshSync();
  }, [refreshSync, transactions, settingsRecord, online]);

  /**
   * Synchronise whenever the app comes to the front.
   *
   * An iPhone Shortcut posts a payment straight to the Worker while this app is
   * closed -- iOS will not run a web app in the background -- so the record
   * exists on the server before this device has ever heard of it. Without an
   * automatic pull that payment stays invisible until someone opens Settings
   * and presses "Sync now", which is not something anyone should have to know.
   *
   * Failures are deliberately silent. Nobody asked for this run, so a flaky
   * connection must not throw an error onto a screen the person was not even
   * looking at. The outcome still lands in the sync state, and Settings still
   * reports it in full.
   */
  const autoSyncRunning = useRef(false);
  const lastAutoSync = useRef(0);

  useEffect(() => {
    if (!ready || !online || !settings.syncEnabled || !settings.apiBaseUrl) return undefined;

    const attempt = async (): Promise<void> => {
      // Returning to the app repeatedly should not mean a request each time.
      if (autoSyncRunning.current) return;
      if (Date.now() - lastAutoSync.current < AUTO_SYNC_MIN_INTERVAL_MS) return;

      autoSyncRunning.current = true;
      lastAutoSync.current = Date.now();
      try {
        await runSync();
      } catch {
        // See above: the sync state carries the failure, not an alert.
      } finally {
        autoSyncRunning.current = false;
        await refreshSync();
      }
    };

    void attempt();

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') void attempt();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [ready, online, settings.syncEnabled, settings.apiBaseUrl, refreshSync]);

  // The theme choice is applied to the document so CSS can act on it, and the
  // status bar colour follows.
  useEffect(() => {
    const root = document.documentElement;
    if (settings.theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', settings.theme);
    }
  }, [settings.theme]);

  const updateSettings = useCallback(async (patch: Partial<Settings>) => {
    await writeSettings(patch);
  }, []);

  const toast = useCallback(
    (message: string, action?: { label: string; onAction: () => void }) => {
      setToastState({ message, ...(action ? { action } : {}), id: Date.now() });
    },
    [],
  );

  const dismissToast = useCallback(() => setToastState(null), []);

  useEffect(() => {
    if (!toastState) return undefined;
    const timer = window.setTimeout(() => setToastState(null), toastState.action ? 6000 : 3200);
    return () => window.clearTimeout(timer);
  }, [toastState]);

  const value = useMemo<AppContextValue>(
    () => ({
      ready,
      settings,
      categories: sortedCategories,
      accounts: accounts ?? [],
      rules: rules ?? [],
      transactions: activeTransactions,
      merchantHistory: merchantHistory ?? [],
      reviewCount,
      sync,
      online,
      updateSettings,
      refreshSync,
      toast,
      dismissToast,
      toastState,
    }),
    [
      ready,
      settings,
      sortedCategories,
      accounts,
      rules,
      activeTransactions,
      merchantHistory,
      reviewCount,
      sync,
      online,
      updateSettings,
      refreshSync,
      toast,
      dismissToast,
      toastState,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error('useApp must be used inside <AppProvider>');
  return value;
}

/** The user's zone, defaulting to Malaysia. Used by every date calculation. */
export function useTimeZone(): string {
  return useApp().settings.timeZone;
}
